import { AccountingResult, AccountingSystem, Bill, InvoiceStatus, InvoiceType, isInvoicePaid } from '@jms/model';

import { defineConditionalTag } from '../../shared/define.conditional.tag';
import { defineStatusTag } from '../../shared/define.status.tag';

// Single source of Bill pill colour. Each pill below feeds BOTH the manifest's board-card tag style
// and the manage surface header's tag deriver, so a Bill cannot render one colour on the card and
// another in the document header.
//
// Bill shows four pills because its state is four independent facts: where the document sits in its
// lifecycle, whether it is a special document type, whether it has been settled, and whether the
// accounting integration accepted it. Only the first is an enum-to-tone map; the other three are
// predicate-gated and only appear when they have something to say.

/** Document lifecycle. `neutral` is also the fallback so unknown/legacy data reads as Draft. */
export const billStatusTag = defineStatusTag<InvoiceStatus, Bill>({
  [InvoiceStatus.Draft]: 'neutral',
  [InvoiceStatus.Approved]: 'brand',
  [InvoiceStatus.Error]: 'error',
  [InvoiceStatus.Void]: 'neutral',
}, 'neutral');

/**
 * Document type. A plain Bill is the unremarkable case and shows no pill at all — only a supplier
 * credit note or a prepayment earns one, because those change how the money reads.
 */
export const billTypeTag = defineConditionalTag<Bill>(bill => {

  switch (bill?.type) {
    case InvoiceType.CreditNoteSupplier:
      return { label: InvoiceType.CreditNoteSupplier, tone: 'warning' };
    case InvoiceType.PrepaymentSupplier:
      return { label: InvoiceType.PrepaymentSupplier, tone: 'brand' };
    default:
      return null;
  }
});

/** Settlement. A supplier credit note is "Settled" rather than "Paid" — money went the other way. */
export const billSettlementTag = defineConditionalTag<Bill>(bill => {

  if (!isInvoicePaid(bill)) return null;
  return { label: bill?.type === InvoiceType.CreditNoteSupplier ? 'Settled' : 'Paid', tone: 'success' };
});

/**
 * Accounting integration result. Silent until a connected system has actually run: Manual still
 * records an approval result, but no ledger received the Bill.
 */
export const billAccountingTag = defineConditionalTag<Bill>(bill => {

  const result = bill?.integration as AccountingResult | null | undefined;
  if (result?.success == null || !isConnectedBillAccountingIntegration(result)) {
    return null;
  }
  return result.success
    ? { label: 'Synced', tone: 'success' }
    // Distinguish the integration failure from the adjacent lifecycle Error pill.
    : { label: 'Accounting error', tone: 'warning' };
});

/** A real ledger connection — Manual is the no-op client used when the billing company has none. */
export function isConnectedBillAccountingIntegration(result: Pick<AccountingResult, 'type'> | null | undefined): boolean {

  return !!result?.type && result.type !== AccountingSystem.Manual;
}
